import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Send } from 'lucide-react';
import { toast } from 'sonner';

const FORMSPREE_ID = 'xpqwkazo';

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    city: '',
    property_type: '',
    monthly_bill_range: '',
    message: '',
    want_assessment: false
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(`https://formspree.io/f/${FORMSPREE_ID}`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          city: formData.city,
          property_type: formData.property_type,
          monthly_bill_range: formData.monthly_bill_range,
          message: formData.message,
          want_assessment: formData.want_assessment ? 'Yes' : 'No'
        })
      });

      if (response.ok) {
        setSubmitted(true);
        toast.success('Thank you! We will contact you within 24 hours.');
        setFormData({
          name: '',
          phone: '',
          email: '',
          city: '',
          property_type: '',
          monthly_bill_range: '',
          message: '',
          want_assessment: false
        });
        setTimeout(() => setSubmitted(false), 5000);
      } else {
        const responseData = await response.json();
        console.error('Formspree error response:', responseData);
        toast.error(responseData.error || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const serviceAreas = [
    'Adilabad', 'Bhadradri Kothagudem', 'Hanumakonda', 'Hyderabad', 'Jagtial',
    'Jangaon', 'Jayashankar Bhupalpally', 'Jogulamba Gadwal', 'Kamareddy', 'Karimnagar',
    'Khammam', 'Kumuram Bheem', 'Mahabubabad', 'Mahabubnagar', 'Mancherial',
    'Medak', 'Medchal-Malkajgiri', 'Mulugu', 'Nagarkurnool', 'Nalgonda',
    'Narayanpet', 'Nirmal', 'Nizamabad', 'Peddapalli', 'Rajanna Sircilla',
    'Rangareddy', 'Sangareddy', 'Siddipet', 'Suryapet', 'Vikarabad',
    'Wanaparthy', 'Warangal', 'Yadadri Bhuvanagiri'
  ];

  return (
    <div className="min-h-screen pt-20">
      <section className="py-20 md:py-32" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h1 className="text-5xl md:text-7xl font-heading font-semibold text-primary tracking-tight leading-[1.1] mb-6">
              Let's Go Solar Together
            </h1>
            <p className="text-lg md:text-xl text-stone-600 leading-relaxed max-w-3xl mx-auto">
              Talk to our solar experts. Get honest advice, transparent pricing, and a custom proposal for your property.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="space-y-8"
            >
              <div>
                <h3 className="text-2xl font-heading font-semibold text-primary mb-6">Contact Information</h3>
                <div className="space-y-6">
                  <a
                    href="tel:+91-9966581466"
                    className="flex items-start space-x-4 p-4 bg-white rounded-2xl border border-stone-100 hover:shadow-card transition-all group"
                    data-testid="contact-phone"
                  >
                    <div className="w-12 h-12 bg-secondary/20 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-secondary/30 transition-colors">
                      <Phone className="w-6 h-6 text-secondary" />
                    </div>
                    <div>
                      <p className="font-medium text-primary">Phone</p>
                      <p className="text-stone-600">+91-9966581466</p>
                      <p className="text-sm text-accent">Click to call</p>
                    </div>
                  </a>

                  <a
                    href="mailto:amigosrenewableenergy@gmail.com"
                    className="flex items-start space-x-4 p-4 bg-white rounded-2xl border border-stone-100 hover:shadow-card transition-all group"
                    data-testid="contact-email"
                  >
                    <div className="w-12 h-12 bg-secondary/20 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-secondary/30 transition-colors">
                      <Mail className="w-6 h-6 text-secondary" />
                    </div>
                    <div>
                      <p className="font-medium text-primary">Email</p>
                      <p className="text-stone-600">amigosrenewableenergy@gmail.com</p>
                      <p className="text-sm text-accent">Send us a message</p>
                    </div>
                  </a>

                  <div className="flex items-start space-x-4 p-4 bg-white rounded-2xl border border-stone-100">
                    <div className="w-12 h-12 bg-secondary/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-secondary" />
                    </div>
                    <div>
                      <p className="font-medium text-primary">Address</p>
                      <p className="text-stone-600">H.no.1-1-27/19/10/a, Prashanth Nagar, Fatimanagar, Warangal</p>
                      <p className="text-stone-600">Hanamkonda, Telangana, India, 506004</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-secondary/10 to-accent/10 rounded-3xl p-6 border border-secondary/20">
                <h4 className="font-heading font-semibold text-primary mb-4">Service Areas</h4>
                <div className="flex flex-wrap gap-2">
                  {serviceAreas.map((city) => (
                    <span key={city} className="px-3 py-1 bg-white text-stone-700 text-sm rounded-full border border-stone-200">
                      {city}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="lg:col-span-2"
            >
              <div className="bg-white rounded-3xl p-8 md:p-12 shadow-hover">
                <h3 className="text-2xl font-heading font-semibold text-primary mb-6">Request Free Solar Quote</h3>
                
                {submitted && (
                  <div className="mb-6 p-4 bg-secondary/10 border border-secondary/30 rounded-2xl text-primary" data-testid="contact-success-message">
                    ✓ Thank you! Our solar expert will contact you within 24 hours.
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-2">Full Name *</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                        required
                        data-testid="contact-name-input"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-2">Phone Number *</label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                        required
                        data-testid="contact-phone-input"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-2">Email *</label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                        required
                        data-testid="contact-email-input"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-2">City *</label>
                      <input
                        type="text"
                        value={formData.city}
                        onChange={(e) => setFormData({...formData, city: e.target.value})}
                        className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                        required
                        data-testid="contact-city-input"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-2">Property Type *</label>
                      <select
                        value={formData.property_type}
                        onChange={(e) => setFormData({...formData, property_type: e.target.value})}
                        className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                        required
                        data-testid="contact-property-input"
                      >
                        <option value="">Select Property Type</option>
                        <option value="Residential">Residential (Home/Villa)</option>
                        <option value="Commercial">Commercial (Office/Factory)</option>
                        <option value="Society">Housing Society</option>
                        <option value="Farmhouse">Farmhouse</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-2">Monthly Bill Range *</label>
                      <select
                        value={formData.monthly_bill_range}
                        onChange={(e) => setFormData({...formData, monthly_bill_range: e.target.value})}
                        className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                        required
                        data-testid="contact-bill-input"
                      >
                        <option value="">Select Bill Range</option>
                        <option value="Below ₹2,000">Below ₹2,000</option>
                        <option value="₹2,000 - ₹5,000">₹2,000 - ₹5,000</option>
                        <option value="₹5,000 - ₹10,000">₹5,000 - ₹10,000</option>
                        <option value="₹10,000 - ₹20,000">₹10,000 - ₹20,000</option>
                        <option value="Above ₹20,000">Above ₹20,000</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">Message (Optional)</label>
                    <textarea
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      rows="4"
                      className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                      placeholder="Tell us more about your requirements..."
                      data-testid="contact-message-input"
                    />
                  </div>

                  <div className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      checked={formData.want_assessment}
                      onChange={(e) => setFormData({...formData, want_assessment: e.target.checked})}
                      className="w-5 h-5 text-primary border-stone-300 rounded focus:ring-2 focus:ring-primary mt-1"
                      data-testid="contact-assessment-checkbox"
                    />
                    <label className="text-sm text-stone-600">
                      I want a free rooftop assessment and site survey
                    </label>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-secondary text-white hover:bg-gradient-to-r hover:from-secondary hover:to-accent rounded-full px-8 py-4 text-lg font-medium transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 flex items-center justify-center"
                    data-testid="contact-submit-button"
                  >
                    {loading ? 'Submitting...' : (
                      <>
                        Request Free Solar Quote <Send className="w-5 h-5 ml-2" />
                      </>
                    )}
                  </button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-secondary/10 to-accent/10">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <h3 className="text-2xl md:text-3xl font-heading font-semibold text-primary mb-4">
            Prefer to Talk Directly?
          </h3>
          <p className="text-lg text-stone-600 mb-8">
            Our solar experts are available Monday to Saturday, 9 AM to 7 PM
          </p>
          <a
            href="https://wa.me/919966581466"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-secondary text-white hover:bg-gradient-to-r hover:from-secondary hover:to-accent rounded-full px-8 py-4 text-lg font-medium transition-all duration-300 shadow-lg hover:shadow-xl"
            data-testid="contact-whatsapp-button"
          >
            Chat on WhatsApp
          </a>
        </div>
      </section>
    </div>
  );
};
